import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { MerchantOnboardingModal } from "../bazaar/MerchantOnboardingModal";
import { Store, Loader2, ExternalLink } from "lucide-react";

export function BazaarTab() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [merchantStatus, setMerchantStatus] = useState(null);
  const [onboarding, setOnboarding] = useState(false);
  const [merchantModalOpen, setMerchantModalOpen] = useState(false);
  const [savingAdult, setSavingAdult] = useState(false);
  const [localAdultContent, setLocalAdultContent] = useState(!!user?.show_adult_content);

  useEffect(() => {
    api.get("/bazaar/merchant/status").then(({ data }) => setMerchantStatus(data)).catch(() => {});
  }, []);

  const toggleAdultContent = async (checked) => {
    setLocalAdultContent(checked);
    setSavingAdult(true);
    try {
      await api.patch("/bazaar/preferences", { show_adult_content: checked });
      await checkAuth();
      toast.success(checked ? "Adult-marked listings will now show in the Bazaar" : "Adult-marked listings are hidden");
    } catch {
      setLocalAdultContent(!checked);
      toast.error("Couldn't update that preference");
    } finally {
      setSavingAdult(false);
    }
  };

  const onboardMerchant = async () => {
    setOnboarding(true);
    try {
      const { data } = await api.post("/bazaar/merchant/onboard", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't start onboarding");
      setOnboarding(false);
    }
  };

  const startBecomeMerchant = () => {
    if (merchantStatus?.merchant_profile) return onboardMerchant();
    setMerchantModalOpen(true);
  };

  return (
    <div data-testid="settings-bazaar-tab" className="space-y-6">
      <div>
        <h2 className="font-display text-2xl mb-1">Bazaar</h2>
        <p className="text-sm text-[#f1e0c5]/50">Marketplace preferences and merchant status.</p>
      </div>

      <div id="settings-section-adult" className="rounded-2xl bg-black/40 border border-white/10 p-6 flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-medium">Show Adult-Marked Listings</p>
          <p className="text-xs text-[#f1e0c5]/50 mt-1">Off by default. Minor accounts can't enable this.</p>
        </div>
        <Switch
          data-testid="settings-adult-content-toggle"
          checked={localAdultContent}
          disabled={savingAdult || user?.is_minor}
          onCheckedChange={toggleAdultContent}
        />
      </div>

      <div id="settings-section-merchant" className="rounded-2xl bg-black/40 border border-white/10 p-6 space-y-4">
        <p className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#f1e0c5]/40"><Store className="w-3.5 h-3.5" /> Merchant Status</p>
        {!merchantStatus ? (
          <Loader2 className="w-5 h-5 animate-spin text-[#D4AF37]" />
        ) : merchantStatus.onboarding_complete ? (
          <p className="text-sm text-[#4d7c0f]" data-testid="settings-merchant-active">You're an active Merchant — manage listings from the Bazaar.</p>
        ) : (
          <Button data-testid="settings-become-merchant-button" onClick={startBecomeMerchant} disabled={onboarding} variant="secondary" className="rounded-full gap-2">
            {onboarding && <Loader2 className="w-4 h-4 animate-spin" />}
            {merchantStatus.has_account ? "Finish Merchant Setup" : "Become a Merchant"}
          </Button>
        )}
      </div>

      <button data-testid="settings-go-to-bazaar-link" onClick={() => navigate("/bazaar")} className="flex items-center gap-2 text-sm text-[#D4AF37] hover:underline">
        <ExternalLink className="w-4 h-4" /> Go to the Bazaar
      </button>

      <MerchantOnboardingModal open={merchantModalOpen} onOpenChange={setMerchantModalOpen} userId={user?.id} defaultEmail={user?.email} onContinueToStripe={onboardMerchant} />
    </div>
  );
}
